import { ArrowRight, BatteryCharging, Cable, House, Sun } from "lucide-react";
import { Link } from "react-router-dom";
import { useSnapshot, useTopology } from "@/store/site";
import { useNow } from "@/hooks/useNow";
import { signedKW, unsignedKW, direction, bessDirection, formatTimeOfDay } from "@/lib/format";
import { useGlossary, type GlossaryKey } from "@/lib/glossary";
import { useT } from "@/lib/i18n";
import { carryTo } from "@/lib/carryParams";
import { Card, CardHeader, CardTitle, GranBadge } from "@/components/ui/Card";
import {
  FreshnessPip,
  classifyAge,
  LIVE_WARN_AFTER_S,
  LIVE_STALE_AFTER_S,
} from "@/components/ui/FreshnessPip";
import { HelpHint } from "@/components/ui/HelpHint";

export function RightNowStrip({ compact = false }: { compact?: boolean } = {}) {
  const snap = useSnapshot();
  const topology = useTopology();
  const t = useT();
  const hasPv = Boolean(snap.pv);
  // Coarse tick is plenty against the 150 s warn threshold, and it keeps
  // the staleness check alive even when the snapshot itself stops updating
  // (which is exactly when it matters).
  const now = useNow(15_000);

  // Classify against the unified idle noise floor. The previous code
  // branched on `> 0` / `< 0`, so the "Idle" sub-label was unreachable
  // for real telemetry — any 1 W of jitter read as "Importing". Now a
  // grid sitting in the noise band correctly reads "Idle", and the
  // tone falls back to neutral so the colour matches the word.
  const gridDir = direction(snap.grid.active_W);
  const bessDir = bessDirection(snap.bess_aggregate.active_W);

  // Drill-down targets (OVW-06). carryTo keeps the operator's time window;
  // when the URL carries no window yet, the Historical links fall back to a
  // sane 24-h preset so the destination doesn't open on its own default.
  const sp = new URLSearchParams(window.location.search);
  const hasWindow = sp.has("start") || sp.has("preset");
  const presetFallback = hasWindow ? {} : { preset: "24h" };

  const cells: CellSpec[] = [
    {
      kind: "grid",
      label: t("monitor.now.grid"),
      icon: Cable,
      tone: gridDir === "idle" ? "muted" : gridDir,
      value: `${signedKW(snap.grid.active_W)}`,
      unit: "kW",
      sub: `${gridDir === "import" ? t("status.importing") : gridDir === "export" ? t("status.exporting") : t("status.idle")} · ${snap.grid.frequency_Hz.toFixed(2)} Hz`,
      help: "grid",
      to: carryTo("/historical", { keys: "grid_W", ...presetFallback }),
      freshKey: "grid.active_W",
    },
    {
      kind: "battery",
      label: t("monitor.now.battery"),
      icon: BatteryCharging,
      tone: bessDir === "idle" ? "muted" : bessDir,
      value: `${signedKW(snap.bess_aggregate.active_W)}`,
      unit: "kW",
      sub: `${bessDir === "charge" ? t("status.charging") : bessDir === "discharge" ? t("status.discharging") : t("status.idle")} · ${snap.bess_aggregate.soc_avg_pct.toFixed(0)}% ${t("monitor.now.avg")} · ${snap.bess_aggregate.online_count}/${topology.bess.length} ${t("status.online")}`,
      help: "soc",
      to: carryTo("/bess"),
      freshKey: "bess_aggregate.active_W",
    },
  ];

  if (hasPv && snap.pv) {
    cells.push({
      kind: "solar",
      label: t("monitor.now.solar"),
      icon: Sun,
      tone: "solar",
      value: `${unsignedKW(snap.pv.active_W)}`,
      unit: "kW",
      sub: `${t("monitor.now.generating")} · ${t("monitor.now.kwhToday", { kwh: snap.pv.today_kWh.toFixed(0) })}`,
      to: carryTo("/historical", { keys: "pv_power_W", ...presetFallback }),
      freshKey: "pv.active_W",
    });
  }

  // Net building load: positive = net consumption (the building is
  // drawing from grid/PV/BESS to power its loads); negative = net
  // production (on-site generation exceeds load, pushing surplus
  // back to the grid). The sub-label flips so the operator can tell
  // at a glance which side of zero the meter is on.
  const loadW = snap.building_load_W;
  const loadDir = loadW > 500 ? "consuming" : loadW < -500 ? "exporting" : "balanced";
  cells.push({
    kind: "netLoad",
    label: t("monitor.now.netBuildingLoad"),
    icon: House,
    tone: loadDir === "exporting" ? "export" : "muted",
    value: `${signedKW(loadW)}`,
    unit: "kW",
    sub: loadDir === "consuming"
      ? t("monitor.now.consuming")
      : loadDir === "exporting"
        ? t("monitor.now.surplusToGrid")
        : t("monitor.now.balanced"),
    help: "netBuildingLoad",
    to: carryTo("/flow"),
    freshKey: "building_load_W",
  });

  // Glance density (F05 / R04): show the primary readings — Grid, Battery,
  // Solar (when present) and the single authoritative Net building-load cell
  // (the "what is the building doing" answer the GM/FM wants at a glance).
  // Detail mode still shows every cell, identical to historical behaviour.
  const shown = compact
    ? cells.filter((c) => c.kind === "grid" || c.kind === "battery" || c.kind === "solar" || c.kind === "netLoad")
    : cells;

  // Responsive column map keyed on the rendered cell count (G02): never
  // auto-fit/auto-fill (which can orphan a lone 5th cell on a 3rd row).
  // Reflow 1→2→3 in the narrow TB column instead of crushing equal cols.
  const gridCols = shown.length >= 5
    ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"   // PV present (5 cells): 1→2→3
    : shown.length === 4
      ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" // no-PV (4 cells): 1→2→4, clean rows at every step
      : "grid-cols-1 sm:grid-cols-2";               // defensive fallback (2-3 cells)

  return (
    <Card className="overflow-hidden">
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle hint={<GranBadge live label={t("monitor.badge.latestUplink")} />}>{t("monitor.now.title")}</CardTitle>
          <FreshnessPip ts={snap.ts} size="xs" className="ml-1" />
        </div>
        <div className="hidden text-[10px] text-text-muted sm:block">
          <span className="text-charge font-semibold">+</span> {t("monitor.legend.importCharge")} ·{" "}
          <span className="text-discharge font-semibold">−</span> {t("monitor.legend.exportDischarge")}
        </div>
      </CardHeader>
      <div className={`grid ${gridCols} gap-px bg-edge`}>
        {shown.map((c, i) => (
          <Cell key={i} {...c} staleTs={staleSourceTs(snap.freshness, c.freshKey, snap.ts, now)} />
        ))}
      </div>
    </Card>
  );
}

interface CellSpec {
  /** Stable cell discriminator (F05) — lets the glance-density filter keep
   *  the primary readings (grid/battery/solar) and drop the net cells
   *  without depending on array position. Optional so the type stays
   *  backwards-safe. */
  kind?: "grid" | "battery" | "solar" | "netLoad" | "netMeter";
  label: string;
  icon: any;
  tone: "import" | "export" | "charge" | "discharge" | "solar" | "muted";
  value: string;
  unit: string;
  sub: string;
  /** Optional glossary entry rendered as a HelpHint next to the label
   *  (CLAR-09 — the three near-identical "now" cells need definitions). */
  help?: GlossaryKey;
  /** Drill-down route — every cell is a Link (OVW-06), carrying the
   *  operator's time window via carryTo at the call site. */
  to: string;
  /** Dotted snapshot path into snapshot.freshness (DATA-08) — must match a
   *  stamp() key in snapshotFromTb.ts. Omitted for derived values that have
   *  no single telemetry source (net at meter), which then fall back to the
   *  snapshot-level ts. */
  freshKey?: string;
}

/**
 * DATA-08 (visible half): resolve a cell's per-value source ts and decide
 * whether it deserves a stale claim. Returns the source ts when the value's
 * age is past the live warn threshold, else null. No map (mock) or missing
 * key → null: absence of freshness data must never LOOK stale.
 */
function staleSourceTs(
  freshness: Record<string, number> | undefined,
  key: string | undefined,
  snapshotTs: number,
  now: number,
): number | null {
  if (!freshness) return null;
  const ts = key !== undefined ? freshness[key] : snapshotTs;
  if (ts === undefined) return null;
  return classifyAge(now - ts, LIVE_WARN_AFTER_S, LIVE_STALE_AFTER_S) !== "fresh" ? ts : null;
}

function Cell({ label, icon: Icon, tone, value, unit, sub, help, to, staleTs }: CellSpec & { staleTs: number | null }) {
  const t = useT();
  const g = useGlossary();
  const colorClass = {
    import: "text-import",
    export: "text-export",
    charge: "text-charge",
    discharge: "text-discharge",
    solar: "text-solar",
    muted: "text-text",
  }[tone];
  return (
    <Link to={to} className="now-cell group relative transition-colors hover:bg-surface-2">
      <div className="now-label">
        <Icon size={12} className={colorClass} />
        {label}
        {help && (
          // HelpHint's trigger button stops propagation itself, but its
          // dismiss-backdrop/close buttons don't — without this guard a
          // click that merely closes the help popover would navigate.
          <span onClick={(e) => e.stopPropagation()}>
            <HelpHint title={g[help].term}>{g[help].def}</HelpHint>
          </span>
        )}
      </div>
      <div
        // Stale value: drop the direction colour for the stale token so the
        // number visibly recedes; the tooltip explains why (DATA-08).
        className={`now-num num ${staleTs !== null ? "text-stale" : colorClass}`}
        title={staleTs !== null
          ? t("monitor.now.staleTitle", { time: formatTimeOfDay(staleTs) })
          : undefined}
      >
        {value}
        <span className="ml-1 text-sm font-normal text-text-muted">{unit}</span>
      </div>
      <div className="now-sub">{sub}</div>
      <ArrowRight
        size={10}
        className="absolute right-3 top-4 text-text-faint opacity-0 transition-opacity group-hover:opacity-100"
      />
    </Link>
  );
}
